import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { UserPlus, UserMinus, AlertTriangle, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useInviteLiveFeed } from "@/hooks/sms/useInviteLiveFeed";
import { buildLiveFeed, type FeedBatch, type LiveFeedKind } from "@/utils/inviteForecast";
import type { MarketingCampaign } from "@/types/sms";

const ICONS: Record<LiveFeedKind, typeof UserPlus> = {
  joined: UserPlus,
  opted_out: UserMinus,
  failed: AlertTriangle,
  batch: Send,
};

const TONES: Record<LiveFeedKind, string> = {
  joined: "text-status-success",
  opted_out: "text-muted-foreground",
  failed: "text-status-danger",
  batch: "text-muted-foreground",
};

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

/** The live feed: joins, opt-outs, batch sends and failures, newest first, in
 *  owner words. Phone numbers are masked to the last four digits. */
export function InviteLiveFeed({ batches }: { batches: MarketingCampaign[] }) {
  const campaignIds = useMemo(() => batches.map((b) => b.id), [batches]);
  const { data: recipients = [] } = useInviteLiveFeed(campaignIds);

  const feedBatches: FeedBatch[] = batches.map((b) => ({
    id: b.id,
    sent_count: b.sent_count,
    created_at: b.created_at,
    effective_send_at: b.effective_send_at,
  }));
  const events = useMemo(() => buildLiveFeed(feedBatches, recipients).slice(0, 12), [feedBatches, recipients]);

  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Live feed</h3>
        {events.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">Activity from invites and replies will show up here.</p>
        ) : (
          <ul className="space-y-2.5">
            {events.map((e) => {
              const Icon = ICONS[e.kind];
              return (
                <li key={e.id} className="flex items-start gap-2.5">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${TONES[e.kind]}`} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">{e.label}</p>
                    <p className="text-xs text-muted-foreground">{relativeTime(e.at)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
